import { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { Paper } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { useDataProvider, useTranslate, useNotify } from "react-admin";
import { queryToExpanded } from "shared/src/utils/decodeQuery";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { useDroppable } from "@dnd-kit/core";

export const CategoryTree = memo(({ handleFilterChange, id }) => {
    const [rootNodes, setRootNodes] = useState([]);
    const [expanded, setExpanded] = useState(queryToExpanded(window.location.hash));
    const filterCategory = id;
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const expandableRef = useRef([]);
    const rootNodesFetched = useRef(false);
    const notify = useNotify();

    const addExpandable = useCallback((id) => {
        if (!expandableRef.current.includes(id)) {
            expandableRef.current.push(id);
        }
    }, []);

    const handleExpansionToggle = useCallback(
        (event, nodeId) => {
            setExpanded((prev) => {
                const newExpanded = prev.includes(nodeId)
                    ? prev.filter((id) => id !== nodeId)
                    : [...prev, nodeId];
                setTimeout(() => {
                    handleFilterChange({ category: filterCategory }, newExpanded);
                }, 0);

                return newExpanded;
            });
        },
        [handleFilterChange, filterCategory]
    );

    const handleSelection = useCallback(
        (event, nodeId, isSelected) => {
            if (!isSelected) {
                return;
            }
            const newCategory = nodeId === "-1" ? "" : nodeId;
            handleFilterChange({ category: newCategory }, expanded);
        },
        [handleFilterChange, expanded]
    );

    const treeSlots = useMemo(
        () => ({
            collapseIcon: IndeterminateCheckBoxIcon,
            expandIcon: AddBoxIcon,
        }),
        []
    );

    useEffect(() => {
        if (rootNodesFetched.current) {
            return;
        }
        rootNodesFetched.current = true;
        dataProvider
            .getRootNodes("categories")
            .then((response) => {
                response.data.forEach((node) => {
                    addExpandable(node.id);
                });
                setRootNodes(response.data);
            })
            .catch((e) => {
                rootNodesFetched.current = false;
                handleHttpError(e, notify);
            });
    }, [addExpandable, dataProvider, notify]);

    return (
        <Paper
            sx={{
                width: "100%",
                height: "90vh",
                padding: "15px 0",
                marginTop: "16px",
            }}
            elevation={1}
        >
            <SimpleTreeView
                slots={treeSlots}
                aria-label={translate("equip.productCategoryTree")}
                expandedItems={expanded}
                onItemExpansionToggle={handleExpansionToggle}
                onItemSelectionToggle={handleSelection}
                selectedItems={filterCategory ? filterCategory : null}
                sx={{
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                }}
                expansionTrigger="iconContainer"
            >
                <DroppableTreeItem itemId={"-1"} label={"--" + translate("equip.all") + "---"} />
                {rootNodes.map((node, index) => (
                    <ParentNode
                        key={node.id}
                        node={node}
                        expandedItems={expanded}
                        itemId={node.id.toString()}
                        addExpandable={addExpandable}
                    />
                ))}
            </SimpleTreeView>
        </Paper>
    );
});

const DroppableTreeItem = memo(({ itemId, label, children, ...props }) => {

    const { setNodeRef, isOver, active } = useDroppable({
        id: itemId,
        data: {
            type: "category",
            name: label,
        },
    });

    const itemStyle = useMemo(
        () => ({
            position: "relative",
            backgroundColor: isOver && active ? "rgba(25, 118, 210, 0.12)" : undefined,
            transition: "background-color 0.2s ease",
        }),
        [isOver, active]
    );

    return (
        <div ref={setNodeRef} style={itemStyle}>
            <TreeItem
                itemId={itemId}
                label={label}
                {...props}
                sx={{
                    "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                }}
            >
                {children}
            </TreeItem>
        </div>
    );
});

const ParentNode = memo(
    ({ node, expandedItems, addExpandable }) => {
        const dataProvider = useDataProvider();
        const [childrenNodes, setChildrenNodes] = useState(null);
        const children = node?.children.length > 0;
        const notify = useNotify();
        const translate = useTranslate();

        const getChildren = useCallback(() => {
            dataProvider
                .getChildNodes("categories", { parentId: node.id })
                .then((response) => {
                    response.data.forEach((node) => {
                        addExpandable(node.id);
                    });
                    setChildrenNodes(response.data);
                })
                .catch((e) => {
                    handleHttpError(e, notify);
                });
        }, [node.id, dataProvider, addExpandable, notify]);

        useEffect(() => {
            if (childrenNodes) {
                return;
            }
            if (expandedItems?.includes(node.id.toString()) && !childrenNodes) {
                getChildren();
            }
        }, [expandedItems, getChildren, node.id, childrenNodes]);

        if (children) {
            return (
                <DroppableTreeItem
                    id={`tree-item-${node.id}`}
                    itemId={`${node.id}`}
                    label={node.name}
                    onClick={getChildren}
                >
                    {childrenNodes ? (
                        childrenNodes.map((childNode) => {
                            return childNode.children.length > 0 ? (
                                <ParentNode
                                    key={childNode.id}
                                    node={childNode}
                                    itemId={`${childNode.id}`}
                                    expandedItems={expandedItems}
                                    addExpandable={addExpandable}
                                />
                            ) : (
                                <DroppableTreeItem
                                    key={childNode.id}
                                    itemId={`${childNode.id}`}
                                    label={childNode.name}
                                />
                            );
                        })
                    ) : (
                        <TreeItem itemId={`0-${node.id}`} />
                    )}
                </DroppableTreeItem>
            );
        }
        return (
            <DroppableTreeItem
                id={`tree-item-${node.id}`}
                itemId={`${node.id}`}
                label={node.id === 1 ? translate("equip.unordered") : node.name}
            />
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.node.id === nextProps.node.id &&
            JSON.stringify(prevProps.expandedItems) === JSON.stringify(nextProps.expandedItems)
        );
    }
);
